/* TCW theme – interactive sections: hero entrance, video reels, featured product, layer guide,
   image hotspots, before/after slider, rating counter, customer videos, video section, related products.
   Each initialiser runs on page load and again when a section is added or changed in the Theme Editor. */
(function () {
  'use strict';
  var theme = window.theme || {};
  var S = theme.strings || {};
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function qsa(root, sel) {
    var list = Array.prototype.slice.call(root.querySelectorAll(sel));
    if (root.matches && root.matches(sel)) list.unshift(root);
    return list;
  }
  function money(cents) { return theme.formatMoney ? theme.formatMoney(cents) : (cents / 100).toFixed(2); }

  /* ---------- Hero: one entrance moment ---------- */
  function initHero(el) {
    requestAnimationFrame(function () { el.classList.add('is-ready'); });
  }

  /* ---------- Featured product: size chips update price, compare-at, dimensions and link ---------- */
  function initSpotlight(sec) {
    if (sec.__tcwInit) return;
    sec.__tcwInit = true;
    var json = sec.querySelector('[data-spot-variants]');
    if (!json) return;
    var variants = JSON.parse(json.textContent);
    var chips = qsa(sec, '.ph-chip');
    var priceEl = sec.querySelector('[data-price]');
    var mrpEl = sec.querySelector('[data-mrp]');
    var dimEl = sec.querySelector('[data-dim]');
    var cta = sec.querySelector('[data-cta]');
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var value = chip.getAttribute('data-size');
        var v = variants.filter(function (x) { return x.option === value && x.available; })[0] || variants.filter(function (x) { return x.option === value; })[0];
        if (!v) return;
        chips.forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
        if (priceEl) priceEl.textContent = money(v.price);
        if (mrpEl) { mrpEl.textContent = money(v.compare); mrpEl.hidden = !(v.compare > v.price); }
        if (dimEl) dimEl.textContent = v.dim || '';
        if (cta) {
          cta.textContent = (cta.getAttribute('data-cta-prefix') || '') + ' ' + value;
          if (v.url && v.url !== '#') cta.setAttribute('href', v.url);
        }
      });
    });
  }

  /* ---------- Layer guide: point at a layer, highlight it on the bed ---------- */
  function initLayers(sec) {
    if (sec.__tcwInit) return;
    sec.__tcwInit = true;
    var bed = sec.querySelector('[data-bed]');
    var layers = qsa(sec, '.ph-layer');
    function highlight(n) {
      if (!bed) return;
      bed.classList.toggle('is-focused', !!n);
      qsa(bed, '.ph-slab').forEach(function (s) { s.classList.toggle('is-active', s.getAttribute('data-layer') === n); });
      layers.forEach(function (l) { l.classList.toggle('is-active', l.getAttribute('data-layer') === n); });
    }
    layers.forEach(function (l) {
      var n = l.getAttribute('data-layer');
      l.addEventListener('mouseenter', function () { highlight(n); });
      l.addEventListener('mouseleave', function () { highlight(null); });
      l.addEventListener('focusin', function () { highlight(n); });
      l.addEventListener('focusout', function () { highlight(null); });
      l.addEventListener('click', function () { highlight(n); });
    });
  }

  /* ---------- Video reels (endless loop, story-style autoplay) ---------- */
  function initReels(reels) {
    if (reels.__tcwInit) return;
    reels.__tcwInit = true;
    var autoplay = reels.getAttribute('data-autoplay') !== 'false';
    var track = reels.querySelector('.ph-reels__track');
    var dotsWrap = reels.querySelector('[data-reel-dots]');
    var originals = Array.prototype.slice.call(track.children);
    var n = originals.length;
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var DWELL = parseInt(reels.getAttribute('data-dwell') || '4500', 10); // ms each photo reel stays centred
    reels.style.setProperty('--ph-dwell', DWELL + 'ms');

    // Copy the set before and after the real reels so the row never runs out on either side
    var makeCopy = function (li) {
      var c = li.cloneNode(true);
      c.classList.add('is-clone');
      c.setAttribute('aria-hidden', 'true');
      c.querySelectorAll('a, button').forEach(function (el) { el.setAttribute('tabindex', '-1'); });
      return c;
    };
    var head = document.createDocumentFragment(), tail = document.createDocumentFragment();
    originals.forEach(function (li) { head.appendChild(makeCopy(li)); tail.appendChild(makeCopy(li)); });
    track.insertBefore(head, track.firstChild);
    track.appendChild(tail);
    var slides = Array.prototype.slice.call(track.children);

    // Dots
    if (!n) return;
    var dots = originals.map(function (li, k) {
      var d = document.createElement('button');
      d.type = 'button';
      d.setAttribute('aria-label', (S.showReel || 'Show reel [index] of [count]').replace('[index]', k + 1).replace('[count]', n));
      if (dotsWrap) dotsWrap.appendChild(d);
      return d;
    });

    var current = -1;
    var state = { hover: false, focus: false, hold: false, offscreen: false };
    var isPaused = function () { return !autoplay || reduceMotion || state.hover || state.focus || state.hold || state.offscreen; };
    var syncPause = function () {
      reels.classList.toggle('is-paused', isPaused());
      reels.classList.toggle('is-offscreen', state.offscreen);
      var v = current > -1 ? slides[current].querySelector('video') : null;
      if (v && v.getAttribute('src')) {
        if (state.offscreen) { v.pause(); } else if (!reduceMotion && v.paused) { v.play().catch(function () {}); }
      }
    };

    var centreOf = function (i) { var s = slides[i]; return s.offsetLeft + s.offsetWidth / 2 - track.clientWidth / 2; };
    var go = function (i, smooth) { track.scrollTo({ left: centreOf(i), behavior: smooth && !reduceMotion ? 'smooth' : 'auto' }); };

    var resetMute = function (s) {
      var v = s.querySelector('video'), b = s.querySelector('.ph-reel__mute');
      if (v) v.muted = true;
      if (b) { b.setAttribute('aria-pressed', 'false'); b.setAttribute('aria-label', S.unmute || 'Unmute'); }
    };

    var activate = function (i) {
      if (i === current) return;
      current = i;
      slides.forEach(function (s, k) {
        var on = k === i;
        var v = s.querySelector('video');
        s.classList.toggle('is-active', on);
        s.classList.remove('is-timed');
        if (!v) return;
        if (on) {
          if (!v.getAttribute('src') && v.dataset.src) { v.src = v.dataset.src; s.classList.add('has-video'); }
          if (v.getAttribute('src')) {
            s.classList.add('is-video-driven');
            try { v.currentTime = 0; } catch (e) {}
            if (!reduceMotion && !state.offscreen) v.play().catch(function () {});
          }
        } else if (v.getAttribute('src')) {
          v.pause();
          resetMute(s);
        }
      });
      var s = slides[i];
      if (!s.classList.contains('is-video-driven')) { void s.offsetWidth; s.classList.add('is-timed'); }
      var real = i % n;
      dots.forEach(function (d, k) { d.setAttribute('aria-current', k === real ? 'true' : 'false'); });
    };

    var nearest = function () {
      var mid = track.scrollLeft + track.clientWidth / 2, best = 0, gap = Infinity;
      slides.forEach(function (s, k) {
        var d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - mid);
        if (d < gap) { gap = d; best = k; }
      });
      return best;
    };

    // After scrolling stops, quietly jump back into the middle copy of the set
    var recentre = function () {
      if (state.hold) { settle = setTimeout(recentre, 150); return; }
      var shift = current < n ? n : (current >= 2 * n ? -n : 0);
      if (!shift) return;
      var target = current + shift;
      track.classList.add('is-jumping');
      track.style.scrollSnapType = 'none';
      track.scrollLeft = centreOf(target);
      activate(target);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          track.style.scrollSnapType = '';
          track.classList.remove('is-jumping');
        });
      });
    };

    var queued = false, settle = null;
    track.addEventListener('scroll', function () {
      if (!queued) {
        queued = true;
        requestAnimationFrame(function () { queued = false; activate(nearest()); });
      }
      clearTimeout(settle);
      settle = setTimeout(recentre, 150);
    }, { passive: true });

    var next = function () { go(current + 1, true); };
    reels.querySelector('[data-reel-prev]').addEventListener('click', function () { go(current - 1, true); });
    reels.querySelector('[data-reel-next]').addEventListener('click', next);
    dots.forEach(function (d, k) {
      d.addEventListener('click', function () {
        var delta = ((k - (current % n)) + n) % n;
        if (delta > n / 2) delta -= n;
        go(current + delta, true);
      });
    });

    // Photo reels move on when their progress bar fills (the bar pauses on hover, so this does too)
    track.addEventListener('animationend', function (e) {
      if (e.animationName !== 'ph-progress') return;
      if (e.target.closest('.ph-reel') !== slides[current] || isPaused()) return;
      next();
    });

    // Clicks: side reel comes to the centre; mute button toggles sound; centre video toggles play
    track.addEventListener('click', function (e) {
      var s = e.target.closest('.ph-reel');
      if (!s) return;
      var k = slides.indexOf(s);
      var mute = e.target.closest('.ph-reel__mute');
      var v = s.querySelector('video');
      if (mute && v) {
        v.muted = !v.muted;
        mute.setAttribute('aria-pressed', v.muted ? 'false' : 'true');
        mute.setAttribute('aria-label', v.muted ? (S.unmute || 'Unmute') : (S.mute || 'Mute'));
        return;
      }
      if (!e.target.closest('.ph-reel__media')) return;
      if (k !== current) { go(k, true); return; }
      if (v && v.getAttribute('src')) { if (v.paused) { v.play().catch(function () {}); } else { v.pause(); } }
    });

    slides.forEach(function (s) {
      var v = s.querySelector('video');
      if (!v) return;
      v.addEventListener('playing', function () { s.classList.add('is-playing'); });
      v.addEventListener('pause', function () { s.classList.remove('is-playing'); });
      v.addEventListener('error', function () { s.classList.remove('is-playing', 'has-video', 'is-video-driven'); });
      v.addEventListener('timeupdate', function () {
        if (v.duration) s.style.setProperty('--ph-p', (v.currentTime / v.duration).toFixed(3));
      });
      v.addEventListener('ended', function () {
        if (s !== slides[current]) return;
        if (isPaused()) { v.currentTime = 0; v.play().catch(function () {}); } else { next(); }
      });
    });

    reels.addEventListener('mouseenter', function () { state.hover = true; syncPause(); });
    reels.addEventListener('mouseleave', function () { state.hover = false; syncPause(); });
    reels.addEventListener('focusin', function (e) {
      var keyboard = true;
      try { keyboard = e.target.matches(':focus-visible'); } catch (err) {}
      if (!keyboard) return;
      state.focus = true; syncPause();
      var s = e.target.closest('.ph-reel');
      if (s && !s.classList.contains('is-clone') && s !== slides[current]) go(slides.indexOf(s), true);
    });
    reels.addEventListener('focusout', function (e) { if (!reels.contains(e.relatedTarget)) { state.focus = false; syncPause(); } });
    track.addEventListener('pointerdown', function () { state.hold = true; syncPause(); });
    window.addEventListener('pointerup', function () { if (state.hold) { state.hold = false; syncPause(); } });
    window.addEventListener('pointercancel', function () { if (state.hold) { state.hold = false; syncPause(); } });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        state.offscreen = !entries[0].isIntersecting; syncPause();
      }, { threshold: 0.35 }).observe(reels);
    }

    var start = n + (parseInt(reels.getAttribute('data-start') || '0', 10) % n);
    go(start, false);
    activate(start);
    syncPause();
    window.addEventListener('resize', function () { go(current, false); });
  }

  /* ---------- Image hotspots: cycle through labels while in view ---------- */
  function initHotspots(sec) {
    if (sec.__tcwInit) return;
    sec.__tcwInit = true;
    var stage = sec.querySelector('[data-hotspot-stage]');
    var spots = stage ? qsa(stage, '.hotspot') : [];
    var dots = qsa(sec, '.cutaway-progress-dot');
    var interval = parseInt(sec.getAttribute('data-interval') || '2400', 10);
    var index = -1, timer = null;
    if (!spots.length) return;
    function keepOnScreen(spot) {
      var label = spot.querySelector('.hotspot-label');
      if (!label) return;
      label.style.setProperty('--shift-x', '0px');
      requestAnimationFrame(function () {
        var r = label.getBoundingClientRect(), shift = 0;
        if (r.left < 12) shift = 12 - r.left; else if (r.right > window.innerWidth - 12) shift = window.innerWidth - 12 - r.right;
        if (shift) label.style.setProperty('--shift-x', shift + 'px');
      });
    }
    function show(i) {
      spots.forEach(function (s, k) { s.classList.toggle('active', k === i); });
      dots.forEach(function (d, k) { d.classList.toggle('active', k === i); });
      if (spots[i]) keepOnScreen(spots[i]);
    }
    function start() {
      if (timer) return;
      index = 0; show(index);
      if (spots.length > 1) timer = setInterval(function () { index = (index + 1) % spots.length; show(index); }, interval);
    }
    function stop() {
      clearInterval(timer); timer = null;
      spots.forEach(function (s) { s.classList.remove('active'); });
      dots.forEach(function (d) { d.classList.remove('active'); });
    }
    spots.forEach(function (s, k) {
      s.addEventListener('mouseenter', function () { clearInterval(timer); timer = null; show(k); });
      s.addEventListener('mouseleave', function () { stop(); start(); });
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) { entries[0].isIntersecting ? start() : stop(); }, { threshold: 0.4 }).observe(stage);
    } else start();
    sec.__tcwShow = function (i) { stop(); show(i); };
  }

  /* ---------- Before / after slider ---------- */
  function initBeforeAfter(sec) {
    if (sec.__tcwInit) return;
    sec.__tcwInit = true;
    var stage = sec.querySelector('[data-ba-stage]');
    var after = sec.querySelector('[data-ba-after]');
    var handle = sec.querySelector('[data-ba-handle]');
    var slider = sec.querySelector('[data-ba-slider]');
    if (!stage || !after || !handle || !slider) return;
    function set(p) {
      p = Math.min(100, Math.max(0, p));
      after.style.clipPath = 'inset(0 ' + (100 - p) + '% 0 0)';
      handle.style.left = p + '%';
    }
    slider.addEventListener('input', function (e) { set(Number(e.target.value)); });
    stage.addEventListener('pointerdown', function (e) {
      var rect = stage.getBoundingClientRect();
      function move(x) { var p = (x - rect.left) / rect.width * 100; set(p); slider.value = String(Math.round(p)); }
      move(e.clientX);
      function onMove(ev) { move(ev.clientX); }
      function onUp() { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); }
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    });
  }

  /* ---------- Rating counter (counts up when it comes into view) ---------- */
  function initCounter(el) {
    if (el.__tcwInit) return;
    el.__tcwInit = true;
    var target = parseFloat(el.getAttribute('data-count-to') || '0');
    var sep = el.getAttribute('data-decimal') || '.';
    if (!target || reduceMotion || !('IntersectionObserver' in window)) return;
    var obs = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      obs.disconnect();
      var t0 = performance.now();
      (function tick(t) {
        var p = Math.min(1, (t - t0) / 900), eased = 1 - Math.pow(1 - p, 3);
        el.textContent = (target * eased).toFixed(1).replace('.', sep);
        if (p < 1) requestAnimationFrame(tick);
      })(t0);
    }, { threshold: 0.5 });
    obs.observe(el);
  }

  /* ---------- Customer videos: play only while visible ---------- */
  var ugcObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { if (e.isIntersecting) { e.target.play().catch(function () {}); } else { e.target.pause(); } });
  }, { threshold: 0.5 }) : null;
  function initUgc(v) {
    if (v.__tcwInit || !ugcObserver || reduceMotion) return;
    v.__tcwInit = true;
    ugcObserver.observe(v);
  }

  /* ---------- Video with text: play / mute buttons, pause when off screen ---------- */
  function initVideoSection(sec) {
    if (sec.__tcwInit) return;
    sec.__tcwInit = true;
    var video = sec.querySelector('[data-video-stage] video');
    var play = sec.querySelector('[data-video-play]');
    var mute = sec.querySelector('[data-video-mute]');
    if (!video) return;
    if (play) play.addEventListener('click', function () {
      video.muted = false; video.play().catch(function () {});
      play.hidden = true;
      if (mute) { mute.textContent = '🔊'; mute.setAttribute('aria-label', S.mute || 'Mute'); }
    });
    if (mute) mute.addEventListener('click', function (e) {
      e.stopPropagation();
      video.muted = !video.muted;
      mute.textContent = video.muted ? '🔇' : '🔊';
      mute.setAttribute('aria-label', video.muted ? (S.unmute || 'Unmute') : (S.mute || 'Mute'));
    });
    if ('IntersectionObserver' in window) new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting && !video.paused) { video.dataset.wasPlaying = 'true'; video.pause(); }
        else if (e.isIntersecting && video.dataset.wasPlaying === 'true') { video.play().catch(function () {}); delete video.dataset.wasPlaying; }
      });
    }, { threshold: 0.3 }).observe(video);
  }

  /* ---------- Related products: arrows + Shopify recommendations ---------- */
  function initRelated(sec) {
    if (sec.__tcwInit) return;
    sec.__tcwInit = true;
    function bindArrows() {
      var row = sec.querySelector('[data-carousel]');
      var prev = sec.querySelector('[data-carousel-prev]');
      var next = sec.querySelector('[data-carousel-next]');
      if (prev) prev.addEventListener('click', function () { if (row) row.scrollBy({ left: -280, behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { if (row) row.scrollBy({ left: 280, behavior: 'smooth' }); });
    }
    var url = sec.getAttribute('data-recommendations-url');
    if (!url) { bindArrows(); return; }
    fetch(url).then(function (r) { return r.text(); }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[data-related]');
      if (fresh && fresh.querySelector('[data-carousel] > *')) {
        sec.innerHTML = fresh.innerHTML;
        if (theme.initReveal) theme.initReveal(sec);
      } else {
        sec.closest('.shopify-section').hidden = true;
      }
      bindArrows();
    }).catch(bindArrows);
  }

  /* ---------- Boot ---------- */
  function init(root) {
    qsa(root, '[data-hero]').forEach(initHero);
    qsa(root, '[data-reels]').forEach(initReels);
    qsa(root, '[data-spotlight]').forEach(initSpotlight);
    qsa(root, '.ph-stack').forEach(initLayers);
    qsa(root, '[data-hotspots]').forEach(initHotspots);
    qsa(root, '[data-before-after]').forEach(initBeforeAfter);
    qsa(root, '[data-count-to]').forEach(initCounter);
    qsa(root, '[data-ugc-video]').forEach(initUgc);
    qsa(root, '[data-video-section]').forEach(initVideoSection);
    qsa(root, '[data-related]').forEach(initRelated);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  // Theme Editor: selecting a hotspot block shows its label
  document.addEventListener('shopify:block:select', function (e) {
    var sec = e.target.closest('[data-hotspots]');
    if (sec && sec.__tcwShow) {
      var spots = Array.prototype.slice.call(sec.querySelectorAll('.hotspot'));
      sec.__tcwShow(spots.indexOf(e.target));
    }
  });
})();
